import { ConfidenceMeter } from "@/components/ConfidenceMeter";

function Note({ text }) {
  if (!text) return null;
  return (
    <div className="flex items-start gap-2 border-l-2 border-monk-blue/50 pl-3 py-1">
      <span className="overline text-monk-blue shrink-0 mt-0.5">Monk</span>
      <p className="hard-data text-[13px] text-neutral-200 leading-relaxed">{text}</p>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="text-[13px]">
      <div className="overline mb-1">{label}</div>
      <div className="text-neutral-200 leading-relaxed">{children}</div>
    </div>
  );
}

const VERDICT_COLOR = {
  authentic: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  inconclusive: "text-monk-warn border-monk-warn/40 bg-monk-warn/10",
  likely_forged: "text-monk-critical border-monk-critical/40 bg-monk-critical/10",
};

export function EngineFindings({ action, name, result }) {
  if (!result) return null;
  return (
    <div className="bg-black/40 border border-white/10 rounded-md p-4 space-y-3" data-testid={`findings-${action}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-heading font-700 text-sm tracking-tight">{name}</span>
        <div className="w-28"><ConfidenceMeter value={result.confidence || 0} /></div>
      </div>
      <Note text={result.monk_note || result.summary} />

      {action === "ocr" && (
        <div className="space-y-3">
          {result.extracted_text && (
            <Row label="Extracted Text">
              <pre className="whitespace-pre-wrap font-mono text-[12px] text-neutral-100 bg-black/50 border border-white/10 rounded-sm p-3 max-h-52 overflow-auto">{result.extracted_text}</pre>
            </Row>
          )}
          {result.restored_text && <Row label="Restored / Reconstructed"><p className="hunch-data">{result.restored_text}</p></Row>}
          {result.legibility_percent != null && <Row label="Legibility"><div className="w-40"><ConfidenceMeter value={result.legibility_percent} /></div></Row>}
          {(result.notes || []).length > 0 && <Row label="Notes"><ul className="list-disc pl-4 space-y-0.5">{result.notes.map((n, i) => <li key={i}>{n}</li>)}</ul></Row>}
        </div>
      )}

      {action === "handwriting" && (
        <div className="space-y-3">
          {result.forgery && (
            <Row label="Forgery Assessment">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[10px] font-mono px-2 py-1 rounded-sm border ${VERDICT_COLOR[result.forgery.verdict] || "text-neutral-300 border-white/15"}`}>{(result.forgery.verdict || "").replace("_", " ").toUpperCase()}</span>
                <span className="font-mono text-[11px] text-neutral-400">{result.forgery.likelihood_percent}% forgery likelihood</span>
              </div>
              {(result.forgery.indicators || []).length > 0 && <ul className="list-disc pl-4 space-y-0.5">{result.forgery.indicators.map((n, i) => <li key={i}>{n}</li>)}</ul>}
            </Row>
          )}
          {(result.characteristics || []).length > 0 && (
            <Row label="Characteristics">
              <div className="space-y-1.5">{result.characteristics.map((c, i) => (
                <div key={i}><span className="text-monk-blue font-mono text-[11px]">{c.trait}</span> — {c.observation}</div>
              ))}</div>
            </Row>
          )}
          {(result.author_traits || []).length > 0 && (
            <Row label="Author Traits"><div className="flex flex-wrap gap-1.5">{result.author_traits.map((t, i) => <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10">{t}</span>)}</div></Row>
          )}
        </div>
      )}

      {action === "cipher" && (
        <div className="space-y-3">
          <Row label="Cipher Type"><span className="hard-data font-medium">{result.cipher_type}</span> {result.solved ? <span className="text-emerald-400 text-[11px] font-mono ml-2">SOLVED</span> : <span className="text-monk-warn text-[11px] font-mono ml-2">UNSOLVED</span>}</Row>
          {result.reasoning && <Row label="Reasoning"><p className="hunch-data">{result.reasoning}</p></Row>}
          {(result.frequency || []).length > 0 && (
            <Row label="Symbol Frequency">
              <div className="space-y-1">{result.frequency.slice(0, 6).map((f, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="font-mono text-[12px] w-8 text-monk-blue">{f.char}</span>
                  <div className="flex-1 h-1.5 bg-white/8 rounded-sm overflow-hidden"><div className="h-1.5 bg-monk-blue" style={{ width: `${Math.min(100, f.pct * 4)}%` }} /></div>
                  <span className="font-mono text-[10px] text-neutral-500 w-10 text-right">{f.pct}%</span>
                </div>
              ))}</div>
            </Row>
          )}
          {result.plaintext_candidate && <Row label="Recovered Fragment"><pre className="whitespace-pre-wrap font-mono text-[12px] text-emerald-300 bg-black/50 border border-white/10 rounded-sm p-3">{result.plaintext_candidate}</pre></Row>}
          {result.decryption_attempt && <Row label="Approach"><p className="hunch-data">{result.decryption_attempt}</p></Row>}
        </div>
      )}

      {action === "linguistic" && (
        <div className="space-y-3">
          {result.authorship && <Row label="Authorship"><p>{result.authorship.profile}</p><p className="hunch-data text-[12px] mt-1">{result.authorship.education_level} · {result.authorship.likely_demographics}</p></Row>}
          {result.psychological && (
            <Row label="Psychological Profile">
              <p>{result.psychological.state_of_mind}</p>
              {result.psychological.risk_assessment && <p className="text-monk-warn text-[12px] mt-1">Risk: {result.psychological.risk_assessment}</p>}
              {(result.psychological.traits || []).length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{result.psychological.traits.map((t, i) => <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10">{t}</span>)}</div>}
            </Row>
          )}
          {(result.linguistic_markers || []).length > 0 && (
            <Row label="Linguistic Markers"><div className="space-y-1">{result.linguistic_markers.map((m, i) => <div key={i}><span className="text-monk-blue font-mono text-[11px]">“{m.marker}”</span> — {m.significance}</div>)}</div></Row>
          )}
          {(result.deception_indicators || []).length > 0 && <Row label="Deception Indicators"><ul className="list-disc pl-4 space-y-0.5 text-monk-warn">{result.deception_indicators.map((n, i) => <li key={i}>{n}</li>)}</ul></Row>}
        </div>
      )}

      {action === "image" && (
        <div className="space-y-3">
          {result.enhancement_note && <Row label="Enhancement"><p>{result.enhancement_note}</p></Row>}
          {(result.figures_or_objects || []).length > 0 && <Row label="Figures / Objects"><ul className="list-disc pl-4 space-y-0.5">{result.figures_or_objects.map((n, i) => <li key={i}>{n}</li>)}</ul></Row>}
          {(result.anomalies || []).length > 0 && (
            <Row label="Anomalies"><div className="space-y-1">{result.anomalies.map((a, i) => <div key={i}><span className="text-monk-blue font-mono text-[11px]">{a.region}</span> — {a.description}</div>)}</div></Row>
          )}
          {(result.hidden_patterns || []).length > 0 && <Row label="Hidden Patterns"><ul className="list-disc pl-4 space-y-0.5">{result.hidden_patterns.map((n, i) => <li key={i}>{n}</li>)}</ul></Row>}
        </div>
      )}
    </div>
  );
}
